import {
  Box,
  Button,
  Flex,
  Heading,
  Skeleton,
  Spacer,
  useToast,
} from '@chakra-ui/react';
import React from 'react';
import { useNavigate, Link } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  FundingRequestStatusEnumGQLType,
  useGetUserActiveCreditFacilitiesQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import {
  dashboardRoutePadding,
  errorToastConfig,
} from 'packages/common-frontend/constants';
import { FundingRequestHistoryViewContainer } from 'packages/funding-requests/ui/history/ViewContainer';
import { baseNewVerificationUrl } from 'packages/verifications/ui/VerificationsTemplate';
import { VerificationTrackerTable } from 'packages/verifications/ui/VerificationTrackerTable';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

const allowedStatuses: FundingRequestStatusEnumGQLType[] = [
  'approved',
  'submitted',
  'canceled',
  'expired',
  'rejected',
];

type Props = { enableVerifications: boolean };
export const FundingRequestHistoryWithVerificationRoute = ({
  enableVerifications,
}: Props) => {
  const toast = useToast();
  const flags = useFeatureFlags();
  const navigate = useNavigate();

  const {
    data: userData,
    loading: userLoading,
  } = useGetUserActiveCreditFacilitiesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      creditFacilitySlugsToHide: flags?.creditFacilitySlugsToHide ?? [],
    },
  });

  if (!userData || !userData.user || userLoading) {
    return <Skeleton data-testid="loading-statements-view" height="600px" />;
  }
  const {
    user: {
      organization: { creditFacilities },
    },
  } = userData;

  return (
    <Box padding={dashboardRoutePadding}>
      <Flex>
        <Heading as="h2" mb="36px">
          Funding Requests
        </Heading>
        <Spacer />
        {enableVerifications && (
          <Box>
            <Button
              backgroundColor={colors.aurora[900]}
              color="white"
              onClick={() => {
                navigate(baseNewVerificationUrl);
              }}
            >
              Start Verification
            </Button>
          </Box>
        )}
      </Flex>
      {enableVerifications && (
        <Box>
          <VerificationTrackerTable
            organizationId={userData.user.organizationId}
          />
          <Box height="32px" />
        </Box>
      )}
      <FundingRequestHistoryViewContainer
        allowedStatuses={allowedStatuses}
        activeCreditFacilities={creditFacilities}
      />
      {enableVerifications && (
        <Box mt="32px" mb="16px" textAlign="right">
          <Link to="/funding-requests/verifications/pipeline/history">
            View all verification reports
          </Link>
        </Box>
      )}
    </Box>
  );
};
